import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'app/rootReducer'
import {
  Formik,
  Field,
  Form,
  FormikHelpers,
  ErrorMessage,
  FormikProps
} from 'formik'
import * as Yup from 'yup'
import Button from 'react-bootstrap/Button'
import {
  UpdateAccountEmailAddressVariables,
  UpdateAccountEmailAddressResponseData
} from 'share/mutations/account'
import { UserProfile } from 'reducers/userprofile/userprofileSlice'
import { Authentication } from 'share/domain/authentication'
import './styles.scss'
import { ButtonsContainer } from 'components/features/dashboard/questionnaire/QuestionnareForm/styles'
import { TwoBtnsContainer } from 'components/atoms/Layout'

interface Props {
  authentication: Authentication
  nextPage(page: string): void
  previousPage(page: string): void
  updateAccountEmailAddressHelper(
    input: UpdateAccountEmailAddressVariables
  ): Promise<UpdateAccountEmailAddressResponseData>
  setSubmitErrorMessage: React.Dispatch<React.SetStateAction<string | null>>
}

interface Values {
  emailAddress: string
}

function getEmptyInitialValues(): Values {
  const initialValues: Values = {
    emailAddress: ''
  }
  return initialValues
}

const validationSchema = Yup.object({
  emailAddress: Yup.string().email('Invalid email format').required('Required')
})

const UserProfileChangeEmailAddressForm = (props: Props): JSX.Element => {
  const {
    authentication,
    nextPage,
    previousPage,
    updateAccountEmailAddressHelper,
    setSubmitErrorMessage
  } = props
  const userprofile = useSelector((state: RootState) => state.userprofile)
  const [loading, setLoading] = useState(true)
  const [formIsDirty, setFormIsDirty] = useState(false)
  const [initialVals, setInitialVals] = useState<Values>(() =>
    getEmptyInitialValues()
  )

  useEffect(() => {
    const initial = setInitialValues(authentication, userprofile)
    setInitialVals(initial)

    setLoading(false)
  }, [])

  function setInitialValues(
    authentication: Authentication,
    userprofile: UserProfile
  ): Values {
    const initialValues: Values = {
      emailAddress:
        authentication.account && authentication.account.emailAddress
          ? authentication.account.emailAddress
          : ''
    }

    if (userprofile.isActive) {
      if (userprofile.emailAddress) {
        initialValues.emailAddress = userprofile.emailAddress
      }

      setFormIsDirty(true)
    }

    return initialValues
  }

  function onSubmitWrapper(
    formik: FormikProps<Values>,
    e: React.FormEvent<HTMLFormElement>
  ) {
    e.preventDefault()

    // only want to save data if something has changed
    if (formik.dirty || formIsDirty) {
      formik.handleSubmit(e)
    } else {
      setSubmitErrorMessage(
        'You have not changed the email address so not update performed.'
      )
    }
  }

  async function onSubmit(
    values: Values,
    { setSubmitting }: FormikHelpers<Values>
  ): Promise<void> {
    const input = {
      updateEmailAddressData: {
        emailAddress: values.emailAddress
      }
    }
    const updateAccountEmailAddressResult =
      await updateAccountEmailAddressHelper(input)
    if (updateAccountEmailAddressResult?.success) {
      nextPage('changeEmailAddressVerify')
    } else {
      setSubmitErrorMessage(
        updateAccountEmailAddressResult?.errorMessage
          ? updateAccountEmailAddressResult?.errorMessage
          : 'unknown error happened'
      )
      setSubmitting(false)
    }
  }

  const onCancel = () => {
    previousPage('edit')
  }

  if (loading) return <div>Loading ...</div>

  return (
    <Formik
      initialValues={initialVals}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {(formik) => {
        return (
          <Form
            className='user-profile-change-email-address-form'
            onSubmit={(e) => onSubmitWrapper(formik, e)}
          >
            <div id='newUserProfileChangeEmailAddressForm'>
              <div className='row'>
                <div className='form-group col-md col-sm-12 mb-0'>
                  <label htmlFor='emailAddress'>
                    New Email Address (User ID)
                  </label>
                  <Field
                    type='text'
                    id='emailAddress'
                    name='emailAddress'
                    data-testid='user-profile-changed-email-address-field'
                    placeholder=''
                    className={
                      formik.errors.emailAddress
                        ? 'form-control is-invalid'
                        : 'form-control'
                    }
                  />
                  <ErrorMessage
                    name='emailAddress'
                    render={(msg) => (
                      <div
                        className='text-danger'
                        data-testid='user-profile-changed-email-address-field-error'
                      >
                        {msg}
                      </div>
                    )}
                  />
                </div>
              </div>
            </div>

            <ButtonsContainer className='p-0 btns-container'>
              <TwoBtnsContainer>
                <Button
                  variant='outline-secondary'
                  onClick={onCancel}
                  className='btn-cancel mt-0'
                >
                  Cancel
                </Button>
                <Button type='submit' className='btn btn-submit w-100'>
                  Next
                </Button>
              </TwoBtnsContainer>
            </ButtonsContainer>
          </Form>
        )
      }}
    </Formik>
  )
}

export default UserProfileChangeEmailAddressForm
